import set from 'lodash/set';
import { orderBy } from 'std-kit';

import { VisualsWithDataTypes } from '../config/constants';
import type { Content } from '../domain/content/entities/valueobjects/Content';
import type { Field, Format } from '../domain/content/entities/valueobjects/Fields';
import type { FontFormat } from '../domain/content/entities/valueobjects/Style';
import type { Column } from '../domain/content/entities/valueobjects/Table';
import type { Column2, HeaderCellDefault, HeaderRowDefault, Table2, TableBorder } from '../domain/content/entities/valueobjects/Table2';
import { cloneObject, deepFilter, isArray, isObject, uuid } from '../utilities';
import { getQuillFromField, getQuillFromText } from './base-objects';
import { getFormatMarginPaddingObject } from './style';

const initLocalStorage = (content) => {
  content.localStorage = {
    unsavedChanges: false,
  };
  return content;
};

/**
 * removes a field from the typeSpecific
 * ATTENTION:
 *  do not use this function if you are not sure if this field
 *  is used as valueField, aggregationField, labelField or something like this
 *  this function cannot handle this atm
 */
const unsafeRemoveField = (content, fieldName) => {
  //TODO remove from dimensions and avalableFields

  //remove from fields
  delete content.typeSpecific.fields[fieldName];
  //remove from tooltipFields
  content.typeSpecific.tooltip.fields = content.typeSpecific.tooltip.fields.filter((el) => el.field !== `data:${fieldName}`);
  const { chart } = content.typeSpecific;
  // chart sorting
  if (chart) {
    const idx = chart.sorting.sortFields.indexOf(fieldName);
    if (idx >= 0) chart.sorting.sortFields.splice(idx, 1, fieldName);
  }

  return content;
};

/**
 * applys new dataSet to existing content
 *
 * for single visuals the information is in   'typeSpecific.updatePossible': {
     dataSetId: newDataSetId,
     dataId: newDataId,
     updated_at: new Date()
   }
 *
 *
 * @param  content complete context object
 * @param options .client wether we use this on server or client
 * @return                           [description]
 */
const applyDataSetUpdateToExistingContent = (content, options) => {
  let hasUpdatedMappingId = false;
  //on client only apply local storage
  if (options.client) {
    if (!content.localStorage) {
      initLocalStorage(content);
    }
    content.localStorage.unsavedChanges = true;
  }

  if (content.typeSpecific.updatePossible) {
    content.typeSpecific.dataId = content.typeSpecific.updatePossible.dataId;
    content.typeSpecific.data.source = `/api/data/${content.typeSpecific.updatePossible.dataId}`;

    if (content.typeSpecific.updatePossible.dataSetFields) {
      const { dataSetFields, oldDataSetFields } = content.typeSpecific.updatePossible;
      for (const fieldName of Object.keys(dataSetFields)) {
        const contentField = content.typeSpecific.fields[fieldName];
        const dataSetField = dataSetFields[fieldName];
        const oldDataSetField = oldDataSetFields ? oldDataSetFields[fieldName] : dataSetField;

        if (!contentField) {
          const fieldToAdd = cloneObject(dataSetField);
          fieldToAdd.isNeededForUniqueness = false;
          fieldToAdd.selectedValues = [];
          content.typeSpecific.fields[fieldToAdd.fieldName] = fieldToAdd;
        } else {
          // always update the field title
          contentField.title = dataSetField.title;

          // field exists, check isoMappingId
          const isIso = contentField.fieldType === 'isoField' || contentField.fieldType === 'isoLabelField';
          const hasMappingIdChanged = oldDataSetField.isoMappingId !== dataSetField.isoMappingId;

          if (isIso && hasMappingIdChanged) {
            console.log('updated mappingId from', oldDataSetField.isoMappingId, '->', dataSetField.isoMappingId);

            contentField.fieldType = dataSetField.fieldType;
            contentField.isoLabelField = dataSetField.isoLabelField;
            contentField.isoIsoField = dataSetField.isoIsoField;
            contentField.isoMappingId = dataSetField.isoMappingId;
            contentField.isNeededForUniqueness = dataSetField.isNeededForUniqueness;
            contentField.selectedValues = [];

            hasUpdatedMappingId = true;
          }

          const isDate = contentField.fieldType === 'timeField';
          const hasFormatChanged = oldDataSetField.format.date.inputFormat !== dataSetField.format.date.inputFormat;

          if (isDate && hasFormatChanged) {
            console.log('updated format from', oldDataSetField.format.date.inputFormat, '->', dataSetField.format.date.inputFormat);

            contentField.format.date = cloneObject(dataSetField.format.date);
            updateAllInlineValueFormats(content, contentField);
          }
        }
      }

      updateTableColumns(content);
      updateTable2Columns(content);
    }

    delete content.typeSpecific.updatePossible;
  }

  return { content, hasUpdatedMappingId };
};

const createNewValueFieldFromExisting = (content, oldValueFieldName, newValueFieldName, newValueFieldTitle, newValueFormat) => {
  const { fields, dimensions } = content.typeSpecific;
  // fields
  fields[newValueFieldName] = cloneObject(fields[oldValueFieldName]);
  fields[newValueFieldName].fieldName = newValueFieldName;
  fields[newValueFieldName].title = newValueFieldTitle;
  fields[newValueFieldName].format = newValueFormat;
  return content;
};

export const hotColToTable2Col = (column: any, idx: number): Column2 => {
  const border: TableBorder = {
    left: { size: 0, color: '#ccc', type: 'solid' },
    top: { size: 0, color: '#ccc', type: 'solid' },
    right: { size: 0, color: '#ccc', type: 'solid' },
    bottom: { size: 0, color: '#ccc', type: 'solid' },
  };

  const tableColumn: Column2 = {
    enabled: true,
    label: column.label,
    quillValue: getQuillFromField(column.field),
    quillBackground: { backgroundColor: '#fff' },
    emptyQuillValue: JSON.stringify({ ops: { insert: '\n' } }),

    fieldName: column.field.fieldName,
    fieldDataType: column.field.dataType,
    fieldType: column.field.fieldType,
    sequence: column.field.sequence ?? idx,
    size: {
      default: {
        flex: 1,
      },
    },
    responsive: {
      collapse: false,
      desktop: true,
      mobile: true,
    },
    border: border,
    ellipsis: false,
    sortOrder: null,
  };

  return tableColumn;
};

// Update Columns with field info
const mapFieldsToColumns = (fields: Field[], columns: Column2[], defaultHeader: HeaderRowDefault) => {
  for (const field of fields) {
    const column: Column2 | undefined = columns.find((col: Column2) => col.fieldName === field.fieldName);
    if (column) {
      // Update Column
      column.fieldType = field.fieldType;
      column.fieldDataType = field.dataType;
      column.label = field.title;
      column.sequence = field.sequence ?? column.sequence;

      // Change Quill [inlineValue] to [dynamicImage] if mediaType is present ?
      if (field.fieldType === 'mediaField') {
        const quill = JSON.parse(column.quillValue);
        for (const op of quill.ops) {
          if (op.insert.inlineValue && op.insert.inlineValue.fieldName === field.fieldName) {
            const mediaoOp = {
              fieldName: field.fieldName,
              imageFormat: { radius: 0, maxHeight: 16, maxWidth: 16 },
              title: field.title,
            };

            op.insert.dynamicImage = mediaoOp;
            delete op.insert.inlineValue;

            break;
          }
        }

        column.quillValue = JSON.stringify(quill);
      }

      // Update Default Header
      const rowHeader = defaultHeader?.items.find((cell) => cell.fieldName === field.fieldName);
      if (rowHeader) {
        rowHeader.sequence = field.sequence ?? rowHeader.sequence;
        rowHeader.fieldName = field.fieldName;
        rowHeader.label = field.title;
        if (!rowHeader.touched) rowHeader.quillValue = getQuillFromText(field.title);
      }
    }
  }
};

export const updateTable2Columns = (content: Content) => {
  // console.log('[updateTable2Columns] update content');
  // if (!VisualsWithDataTypes.includes(content.type)) return;
  if (content.type !== 'table2') return;
  if (!content.typeSpecific.table2) return;

  const table2 = content.typeSpecific.table2;

  const fieldsArray = Object.values(content.typeSpecific.fields!);
  const fields: Field[] = fieldsArray.filter((obj) => obj.fieldType !== 'isoField' && !obj.autoGenerated);
  const columns: Column2[] = table2.columns.items.filter((col) => col.fieldName !== 'index');
  const defaultHeader = table2.header.rows.find((row) => row.id === 'default') as HeaderRowDefault;
  const headers: HeaderCellDefault[] = defaultHeader.items;
  const fixedColumns = 1;

  // IN fieldsArray but not in columns
  const fieldsToAdd = fields.filter((field: Field) => !columns.map((col) => col.fieldName).includes(field.fieldName));
  // IN columns but not in fieldsArray
  const fieldsToDelete = columns.filter((col: Column2) => !fields.map((field) => field.fieldName).includes(col.fieldName));

  // Update Columns (type, etc)
  mapFieldsToColumns(fields, columns, defaultHeader);

  // Added Columns
  if (fieldsToAdd.length > 0) {
    const addColumns: Column2[] = [];
    const addHeaders: HeaderCellDefault[] = [];

    let fieldIdx = 0;
    for (const field of fieldsToAdd) {
      const border: TableBorder = {
        left: { size: 1, color: '#ccc', type: 'solid' },
        right: { size: 0, color: '#ccc', type: 'solid' },
        top: { size: 0, color: '#ccc', type: 'solid' },
        bottom: { size: 0, color: '#ccc', type: 'solid' },
      };

      const column: Column2 = {
        enabled: true,
        label: field.title,
        quillValue: getQuillFromField(field),
        quillBackground: { backgroundColor: '#fff' },
        emptyQuillValue: JSON.stringify({ ops: { insert: '\n' } }),

        fieldName: field.fieldName,
        fieldDataType: field.dataType,
        fieldType: field.fieldType,
        sequence: field.sequence ?? columns.length + fieldIdx - fixedColumns,
        size: {
          default: {
            flex: 1,
          },
        },
        responsive: {
          collapse: false,
          desktop: true,
          mobile: true,
        },
        border: border,
        ellipsis: false,
        sortOrder: null,
      };

      addColumns.push(column);

      // Add Header
      const headerCell: HeaderCellDefault = {
        id: `default-${uuid()}`,
        label: field.title,
        fieldName: field.fieldName,
        sequence: field.sequence ?? columns.length - fixedColumns,
        quillValue: getQuillFromText(field.title),
        // fontFormat: defaultStyle,
        colSpan: 1,
        rowSpan: 1,
      };

      addHeaders.push(headerCell);
      fieldIdx++;
    }

    const totalColumns = columns.concat(addColumns);
    const sortedColumns = orderBy(totalColumns, ['sequence'], ['asc'], false);
    content.typeSpecific.table2.columns.items = sortedColumns;

    const totalHeaders = headers.concat(addHeaders);
    const sortedHeaders = orderBy(totalHeaders, ['sequence'], ['asc'], false);
    defaultHeader.items = sortedHeaders;
  }

  // Removed Columns
  if (fieldsToDelete.length > 0) {
    for (const field of fieldsToDelete) {
      const columnIdx = table2.columns.items.findIndex((col: Column2) => col.fieldName === field.fieldName);
      if (columnIdx > -1) table2.columns.items.splice(columnIdx, 1);

      const headerIdx = defaultHeader.items.findIndex((headerCell: HeaderCellDefault) => headerCell.fieldName === field.fieldName);
      if (headerIdx > -1) defaultHeader.items.splice(headerIdx, 1);
    }
  }
};

export const updateTableColumns = (content: Content) => {
  // console.log('[content utilties] update table columns');
  if (!VisualsWithDataTypes.includes(content.type)) return;
  const padding = getFormatMarginPaddingObject(10, 'px', 5, 'px', 10, 'px', 5, 'px');

  const fieldsArray = Object.values(content.typeSpecific.fields!);
  const fields = fieldsArray.filter((obj: any) => obj.fieldType !== 'isoField' && !obj.autoGenerated);

  if (content?.typeSpecific?.table?.columns) {
    for (const field of fields) {
      // check if the field is already in table columns
      if (content.typeSpecific.table.columns.some((col) => col.definition.field === field.fieldName)) continue;

      // add new column
      const column = {
        enabled: true,
        autoIdx: false,
        sortOrder: null,
        definition: {
          field: field.fieldName,
          headerName: field.title,
          type: [],
          dataType: 'text', // Default
          suppressSizeToFit: false,
        },
        size: {
          flex: 0,
          minWidth: 64,
          maxWidth: 0,
        },
        style: {
          font: null, // getExtendedFormatObject(undefined, undefined, undefined, undefined, undefined, undefined, padding, undefined),
          background: null /*{
            backgroundColor: 'rgba(255, 255, 255, 0)',
          },*/,
          border: null, // getBorderObject('#f1f1f1','none', 0),
          format: cloneObject(field.format),
        },
      };

      content.typeSpecific.table.columns.push(column as unknown as Column);
    }
  }
};

/**
 * sets a New Value Field for
 * @param  content contentItem with typeSpecific in it
 * @param oldValueFieldName String name of the old value field that was set before
 * @param newValueFieldName - String unique identifier
 * @param newValueFieldTitle - String title that is displayed in editor
 * @param newValueFormat - formatObject
 * @return content object changed
 */
const setNewValueField = (content, oldValueFieldName, newValueFieldName, newValueFieldTitle, newValueFormat) => {
  //console.log("Setting new Value Field: ",content,oldValueFieldName, newValueFieldName, newValueFieldTitle, newValueFormat);
  const { chart, fields, map, tooltip, dimensions, style } = content.typeSpecific;
  //dimensions
  createNewValueFieldFromExisting(content, oldValueFieldName, newValueFieldName, newValueFieldTitle, newValueFormat);
  for (const dimension of dimensions) {
    if (dimension.requirements.fieldTypes.includes('valueField')) {
      //console.log("[typeSpecificChangeHelper] - Applying ",dimension," newValueFieldName: ",newValueFieldName, content);
      dimension.fieldName = newValueFieldName;
      dimension.possibleFields = [];
      //update Possible Field Names for this
      for (const fieldName of Object.keys(fields)) {
        const field = fields[fieldName];
        if (field.dataType === 'number') {
          dimension.possibleFields.push({
            fieldName: field.fieldName,
            title: field.title,
          });
        }
      }
    }
  }
  // chart axis
  chart.axis.valueAxis.title = ''; //newValueFieldTitle;
  chart.axis.valueAxis.format = newValueFormat;

  // chart sorting
  if (chart) {
    const idx = chart.sorting.sortFields.indexOf(oldValueFieldName);
    if (idx >= 0) chart.sorting.sortFields.splice(idx, 1, newValueFieldName);
    //chart.values.format = newValueFormat;
  }
  if (map?.legend?.values) {
    map.legend.values.format = newValueFormat;
  }

  // tooltip
  tooltip.value.label = ''; // newValueFieldTitle;
  tooltip.value.field = `data:${newValueFieldName}`;
  tooltip.value.format = newValueFormat;

  //coloring
  if (style) {
    style.colors2.fieldName = newValueFieldName;
  }

  return content;
};

export const getQuillObject = (quillStr: string) => {
  try {
    if (typeof quillStr === 'string' && quillStr?.startsWith('{') && quillStr?.endsWith('}')) {
      const obj = JSON.parse(quillStr);
      if (isArray(obj?.ops)) return obj;
    }
  } catch (error) {
    console.log('[getQuillObject] error:', error);
  }
};

export const convertQuillToString = (quillStr: string) => {
  try {
    const quill = getQuillObject(quillStr);
    return quill.ops.reduce((text, op) => {
      if (!op.insert) throw new TypeError('only `insert` operations can be transformed!');
      if (typeof op.insert !== 'string') return `${text} `;
      return text + op.insert;
    }, '');
  } catch (error) {
    return quillStr;
  }
};

export const formatFilterFn = (value: any) => isObject(value) && 'formatType' in value && 'locale' in value;
export const styleFilterFn = (value: any) => isObject(value) && ('fontFamily' in value || 'font' in value) && ('color' in value || 'weight' in value);
export const quillFilterFn = (value: any) => isObject(getQuillObject(value));

export const getFormatObjects = (obj: object | []) => deepFilter<Format>(obj, formatFilterFn);
export const getStyleObjects = (obj: object | []) => deepFilter<FontFormat>(obj, styleFilterFn);
export const getQuillObjects = (obj: object | []) => deepFilter<any>(obj, quillFilterFn);

export const updateQuillObject = (quillStr: string, callback: Function): string => {
  const obj = getQuillObject(quillStr);
  if (obj) {
    try {
      const updatedObj = callback(obj);
      return updatedObj ? JSON.stringify(updatedObj) : quillStr;
    } catch (error) {
      console.log('[updateQuillObject] error:', error);
    }
  }

  return quillStr;
};

export const updateAllQuillObjects = (content, callback: Function) => {
  const quillRefs = deepFilter<any>(content, quillFilterFn, { path: true });
  for (const [path, quillStr] of quillRefs) {
    try {
      const updatedObj = callback(JSON.parse(quillStr));
      if (updatedObj) {
        const updatedQuill = JSON.stringify(updatedObj);
        set(content, path, updatedQuill);
      }
    } catch (error) {
      console.log('[updateAllQuillObjects] error:', error, path, quillStr);
    }
  }
};

export const updateInlineValueFormat = (quillObj: { ops: any[] }, field: Field) => {
  for (const op of quillObj.ops) {
    if (op.insert.hasOwnProperty('inlineValue')) {
      const { fieldName } = op.insert.inlineValue;
      if (fieldName === field.fieldName) {
        op.insert.inlineValue.format = cloneObject(field.format);
      }
    }
  }

  return quillObj;
};

export const updateAllInlineValueFormats = (content, field: Field) => {
  updateAllQuillObjects(content, (quillObj) => updateInlineValueFormat(quillObj, field));
};

export const getContentChildren = (content) => {
  const children: string[] = [];

  if (content?.type === 'atlas') {
    for (const child of content.typeSpecific.atlas.children) {
      children.push(child.slug);
    }
  }

  if (content?.type === 'dash') {
    for (const child of content.typeSpecific.dash.children) {
      children.push(child.slug);
    }
  }

  if (content?.type === 'report') {
    for (const chapter of content.typeSpecific.report.chapters) {
      for (const child of chapter.children) {
        children.push(child.slug);
      }
    }
  }

  return children;
};

export { applyDataSetUpdateToExistingContent, createNewValueFieldFromExisting, initLocalStorage, setNewValueField, unsafeRemoveField };
